import type {Feature} from '@kakaoent/feature-flag';
import cookie from 'cookie';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {COOKIE_KEY_OVERRIDE_FEATURE} from '../common/constant';
import {isTrue} from '../common/shared/utils/isTrue';
import {Button} from '../design-system/atoms/Button/Button';
import {Text} from '../design-system/atoms/Text/Text';

type DeveloperFeatureOverrideModalProps = {
  features: Feature;
  onClose: () => void;
};

const COOKIE_PATH = 'path=/;';
type BooleanFeatureMap = Record<string, boolean>;

const formatStatus = (value: boolean) => (value ? 'On' : 'Off');

const parseOverrideCookie = () => {
  if (typeof document === 'undefined') {
    return {};
  }
  const parsedCookie = cookie.parse(document.cookie);
  const override = parsedCookie[COOKIE_KEY_OVERRIDE_FEATURE];
  if (!override) {
    return {};
  }
  return override.split('&').reduce<Record<string, boolean>>((acc, pair) => {
    const [rawKey, rawValue] = pair.split('=');
    if (!rawKey || rawValue === undefined) {
      return acc;
    }
    acc[rawKey] = isTrue(rawValue);
    return acc;
  }, {});
};

const serializeOverrideMap = (overrides: Record<string, boolean>) => {
  const keys = Object.keys(overrides).sort();
  return keys.map(key => `${key}=${overrides[key]}`).join('&');
};

const writeOverrideCookie = (overrides: BooleanFeatureMap) => {
  if (typeof document === 'undefined') {
    return;
  }
  const serialized = serializeOverrideMap(overrides);
  if (!serialized) {
    document.cookie = `${COOKIE_KEY_OVERRIDE_FEATURE}=; ${COOKIE_PATH} Max-Age=0;`;
    return;
  }
  document.cookie = `${COOKIE_KEY_OVERRIDE_FEATURE}=${serialized}; ${COOKIE_PATH}`;
};

export function DeveloperFeatureOverrideModal({
  features,
  onClose,
}: DeveloperFeatureOverrideModalProps) {
  const booleanFeatureMap = useMemo(() => {
    return Object.entries(features).reduce<BooleanFeatureMap>(
      (acc, [key, value]) => {
        if (typeof value === 'boolean') {
          acc[key] = value;
        }
        return acc;
      },
      {}
    );
  }, [features]);

  const sortedFeatureKeys = useMemo(
    () => Object.keys(booleanFeatureMap).sort(),
    [booleanFeatureMap]
  );

  const baseFeatureMap = useMemo(() => {
    return sortedFeatureKeys.reduce<BooleanFeatureMap>((acc, key) => {
      acc[key] = booleanFeatureMap[key];
      return acc;
    }, {});
  }, [booleanFeatureMap, sortedFeatureKeys]);

  const [overrideMap, setOverrideMap] = useState<BooleanFeatureMap>(() => {
    const overrideCookie = parseOverrideCookie();
    return sortedFeatureKeys.reduce<BooleanFeatureMap>((acc, key) => {
      acc[key] =
        overrideCookie[key] !== undefined
          ? overrideCookie[key]
          : booleanFeatureMap[key];
      return acc;
    }, {});
  });

  useEffect(() => {
    const overrideCookie = parseOverrideCookie();
    setOverrideMap(
      sortedFeatureKeys.reduce<BooleanFeatureMap>((acc, key) => {
        acc[key] =
          overrideCookie[key] !== undefined
            ? overrideCookie[key]
            : booleanFeatureMap[key];
        return acc;
      }, {})
    );
  }, [booleanFeatureMap, sortedFeatureKeys]);

  const handleToggleOverride = useCallback((key: string) => {
    setOverrideMap(prev => {
      const nextValue = !prev[key];
      const next = {...prev, [key]: nextValue};
      writeOverrideCookie(next);
      return next;
    });
  }, []);

  const handleReset = useCallback(() => {
    setOverrideMap({...baseFeatureMap});
    if (typeof document !== 'undefined') {
      document.cookie = `${COOKIE_KEY_OVERRIDE_FEATURE}=; ${COOKIE_PATH} Max-Age=0;`;
    }
    window.location.reload();
  }, [baseFeatureMap]);

  const handleConfirm = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-100001 flex items-center justify-center bg-[rgb(var(--colors-theme-solid-0)_/_80%)]"
    >
      <div className="max-h-80vh w-90vw max-w-480px overflow-y-auto rounded-12px bg-el-100 p-24px shadow-lg text-theme-solid-60">
        <header className="mb-16px">
          <Text className="font-bold text-18px">임시 피쳐 제어</Text>
          <br />
          <Text className="text-12px text-red-500">
            오버라이드 값은 브라우저 새로고침 후 적용됩니다.
          </Text>
          <br />
          <Text className="text-12px text-blue-500 underline">
            <a href="/dev/feature" target="_blank">
              /dev/feature 에서 확인 가능합니다
            </a>
          </Text>
        </header>
        <ul className="mb-24px flex flex-col gap-12px">
          {sortedFeatureKeys.map(key => (
            <li
              key={key}
              className="flex items-center justify-between gap-12px"
            >
              <div className="flex flex-col">
                <Text className="font-medium">{key}</Text>
              </div>
              <div className="flex items-center gap-8px">
                <Button
                  data-testid={`feature-override-${key}`}
                  onClick={() => handleToggleOverride(key)}
                >
                  {formatStatus(overrideMap[key])}
                </Button>
              </div>
            </li>
          ))}
        </ul>
        <div className="flex justify-end gap-12px">
          <Button onClick={handleReset}>초기화</Button>
          <Button onClick={handleConfirm}>확인</Button>
        </div>
      </div>
    </div>
  );
}
